import ConfirmDialog from "@components/ConfirmDialog";
import Label from "@components/Label";
import {
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCommonStyles } from "@pages/ManualDocumentV2/hooks/useCommonStyles";
import { Pencil, Trash } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import useLocales from "src/hooks/useLocales";

interface ILineItemsTableRowProps {
  row: any;
  index: number;
  itemRemove: () => void;
  itemUpdate: () => void;
}

export const LineItemsTableRow = ({
  row,
  itemRemove,
  itemUpdate,
}: ILineItemsTableRowProps) => {
  const { translate } = useLocales("manual_document_screen");
  const theme = useTheme();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const {
    description,
    referenceCode,
    quantity,
    unitPrice,
    taxType,
    vatPercentage,
    deliveryNote,
    poNumber,
    dto,
    dtoType,
  } = row;
  const { tableRow } = useCommonStyles();
  const { watch } = useFormContext();
  const currency = watch("currency") ?? "";
  const headerPoNumber = watch("poNumber");
  const headerDeliveryNote = watch("deliveryNote");
  const isHeaderPOToggleOn = watch("isHeaderPOToggleOn");
  const isHeaderDNToggleOn = watch("isHeaderDNToggleOn");
  const hideItemPO = isHeaderPOToggleOn || !!headerPoNumber;
  const hideItemDN = isHeaderDNToggleOn || !!headerDeliveryNote;

  const gross = unitPrice && quantity ? unitPrice * quantity : 0;

  const discountAmount =
    dto != null && dto > 0
      ? dtoType === "percentage"
        ? (dto / 100) * gross
        : dto
      : 0;

  const discountedBase = Math.max(0, gross - discountAmount);
  const totalVat = vatPercentage ? (vatPercentage / 100) * discountedBase : 0;
  const grossWithVat =
    gross + (vatPercentage ? (vatPercentage / 100) * gross : 0);
  const discountedTotal = discountedBase + totalVat;

  const discountLabel =
    dto != null && dto > 0
      ? dtoType === "percentage"
        ? `${dto}% off`
        : `${currency} ${dto} off`
      : null;

  const handleConfirmDelete = () => {
    itemRemove();
    setIsConfirmOpen(false);
  };

  const [tooltipEnabled, setTooltipEnabled] = useState(false);

  const handleShowTooltip = ({ currentTarget }: React.MouseEvent<Element>) => {
    const isOverflowing =
      currentTarget.scrollWidth > currentTarget.clientWidth ||
      currentTarget.scrollHeight > currentTarget.clientHeight;
    setTooltipEnabled(isOverflowing);
  };

  const hideTooltip = () => setTooltipEnabled(false);

  return (
    <>
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={translate("item_delete_title")}
        content={translate("item_delete_warning")}
        onConfirm={handleConfirmDelete}
        confirmLoading={false}
        isManualFormComponent={true}
      />
      <TableRow
        hover
        sx={{
          "& td": {
            borderTop: `1px solid ${theme.palette.text.borderLine}`,
          },
        }}
      >
        <TableCell>
          <Tooltip
            title={description}
            onMouseEnter={handleShowTooltip}
            onMouseLeave={hideTooltip}
            disableHoverListener={!tooltipEnabled}
            placement="top"
          >
            <Typography
              color={theme.palette.text.primary}
              sx={{
                fontSize: "12px",
                width: 230,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {description}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          <Stack
            direction="column"
            spacing={0.5}
            sx={{ alignItems: "flex-end", justifyContent: "flex-start" }}
          >
            <Tooltip
              title={referenceCode}
              onMouseEnter={handleShowTooltip}
              onMouseLeave={hideTooltip}
              disableHoverListener={!tooltipEnabled}
              placement="top"
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  textOverflow: "ellipsis",
                  maxWidth: 80,
                }}
              >
                {referenceCode}
              </Typography>
            </Tooltip>

            {!hideItemPO && poNumber && (
              <Label color="primary" variant="outlined">
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", justifyContent: "flex-end" }}
                >
                  {/* <Typography variant="body2" color="text.secondary">
                    PO-
                  </Typography> */}
                  <Tooltip
                    title={poNumber}
                    onMouseEnter={handleShowTooltip}
                    onMouseLeave={hideTooltip}
                    disableHoverListener={!tooltipEnabled}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        maxWidth: 140,
                      }}
                    >
                      {poNumber.toUpperCase().startsWith("PO")
                        ? poNumber
                        : `PO-${poNumber}`}{" "}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Label>
            )}

            {!hideItemDN && deliveryNote && (
              <Label color="primary" variant="outlined">
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  {/* <Typography variant="body2" color="text.secondary">
                    DN-
                  </Typography> */}
                  <Tooltip
                    title={deliveryNote}
                    onMouseEnter={handleShowTooltip}
                    onMouseLeave={hideTooltip}
                    disableHoverListener={!tooltipEnabled}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        maxWidth: 140,
                      }}
                    >
                      {deliveryNote.toUpperCase().startsWith("DN")
                        ? deliveryNote
                        : `DN-${deliveryNote}`}{" "}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Label>
            )}
          </Stack>
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>{quantity}</TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          {unitPrice?.toLocaleString()}
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          {taxType
            ? `${taxType}(${vatPercentage ?? 0}%)`
            : vatPercentage
            ? `VAT(${vatPercentage}%)`
            : "-"}
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          {discountLabel ? (
            <>
              <div>{discountedTotal.toLocaleString()}</div>
              <div
                style={{
                  textDecoration: "line-through",
                  color: "rgba(0,0,0,0.45)",
                }}
              >
                {grossWithVat.toLocaleString()}
              </div>
              <Label color="success">{discountLabel}</Label>
            </>
          ) : (
            <div>{discountedTotal.toLocaleString()}</div>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            <IconButton onClick={itemUpdate}>
              <Pencil strokeWidth="1.5px" size="18px" />
            </IconButton>
            <IconButton onClick={() => setIsConfirmOpen(true)}>
              <Trash strokeWidth="1.5px" size="18px" />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
};
